import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save, Trash2, MapPin, Pencil } from "lucide-react";
import api from "@/lib/api";

// leaflet-draw s'accroche au L global : on l'expose AVANT de charger le plugin.
// (require non hoisté → exécuté après l'affectation window.L, contrairement à import)
if (typeof window !== "undefined") {
  window.L = L;
  // eslint-disable-next-line global-require
  require("leaflet-draw");
}

/**
 * Dialog de délimitation d'une zone terrain (le responsable encadre le secteur
 * où vont ses RD). Carte Leaflet + fond OpenStreetMap (gratuit, sans clé) et outil
 * de dessin de polygone. Un seul secteur par cellule (ville / jour). La zone est
 * enregistrée sur la cellule (`geo_zone` GeoJSON) via POST /pap/cell/{id}/zone.
 */
export const ZoneMapDialog = ({ open, onClose, cell, ligne, ville, onSaved }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawnRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [hasZone, setHasZone] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let tries = 0;

    const doInit = (el) => {
      if (cancelled || el._leaflet_id || mapRef.current) return;
      try {
        const map = L.map(el, { center: [46.6, 2.4], zoom: 6, scrollWheelZoom: true });
        mapRef.current = map;
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19, attribution: "&copy; OpenStreetMap",
        }).addTo(map);

        const drawn = new L.FeatureGroup();
        map.addLayer(drawn);
        drawnRef.current = drawn;

        const geo = cell?.geo_zone;
        if (geo?.coordinates?.[0]?.length) {
          const latlngs = geo.coordinates[0].map(([lng, lat]) => [lat, lng]);
          const poly = L.polygon(latlngs, { color: "#EE722E", weight: 2, fillOpacity: 0.18 });
          drawn.addLayer(poly);
          setHasZone(true);
          try { map.fitBounds(poly.getBounds(), { padding: [24, 24] }); } catch (e) { /* noop */ }
        } else if (geo?.center) {
          map.setView([geo.center[0], geo.center[1]], 13);
        }

        if (L.Control && L.Control.Draw) {
          const drawControl = new L.Control.Draw({
            position: "topleft",
            draw: {
              polygon: { shapeOptions: { color: "#EE722E", weight: 2, fillOpacity: 0.18 }, allowIntersection: false, showArea: false },
              polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false,
            },
            edit: { featureGroup: drawn, remove: true },
          });
          map.addControl(drawControl);
          map.on(L.Draw.Event.CREATED, (e) => { drawn.clearLayers(); drawn.addLayer(e.layer); setHasZone(true); });
          map.on(L.Draw.Event.DELETED, () => setHasZone(drawn.getLayers().length > 0));
        }

        setReady(true);
        // Le conteneur peut n'obtenir sa taille qu'après l'animation du dialog.
        setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* noop */ } }, 200);
        setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* noop */ } }, 600);

        if (!geo && ville) {
          fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=fr&q=${encodeURIComponent(ville)}`,
            { headers: { "Accept-Language": "fr" } })
            .then((r) => r.json())
            .then((arr) => { if (!cancelled && arr?.[0] && mapRef.current) mapRef.current.setView([parseFloat(arr[0].lat), parseFloat(arr[0].lon)], 13); })
            .catch(() => { /* réseau indisponible : on reste sur la France */ });
        }
      } catch (err) {
        console.error("ZoneMapDialog init error:", err);
        toast.error("Impossible d'initialiser la carte.");
      }
    };

    // Attend que le conteneur soit monté dans le portail Radix (polling court).
    const tick = () => {
      if (cancelled) return;
      const el = containerRef.current;
      if (el) { doInit(el); return; }
      if (tries++ < 40) setTimeout(tick, 80);
    };
    const t = setTimeout(tick, 60);

    return () => {
      cancelled = true;
      clearTimeout(t);
      if (mapRef.current) { try { mapRef.current.remove(); } catch (e) { /* noop */ } }
      mapRef.current = null; drawnRef.current = null;
      setReady(false); setHasZone(false);
    };
  }, [open, cell?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const extractGeometry = () => {
    const drawn = drawnRef.current;
    if (!drawn) return null;
    const layers = drawn.getLayers();
    if (!layers.length) return null;
    try { return layers[0].toGeoJSON().geometry; } catch (e) { return null; }
  };

  const save = async () => {
    if (!cell?.id) return;
    setSaving(true);
    try {
      const geo = extractGeometry();
      let center = null;
      const layers = drawnRef.current?.getLayers() || [];
      if (geo && layers.length) {
        const c = layers[0].getBounds().getCenter();
        center = [c.lat, c.lng];
      }
      await api.post(`/pap/cell/${cell.id}/zone`, { geo_zone: geo, center });
      toast.success(geo ? "Zone enregistrée" : "Zone effacée");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const clearZone = () => { drawnRef.current?.clearLayers(); setHasZone(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="pap-zone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[var(--shariti-orange)]" />
            Zone terrain — {cell?.ville || ville}
            {ligne?.name ? <span className="text-sm font-normal text-muted-ink">· {ligne.name}</span> : null}
          </DialogTitle>
        </DialogHeader>
        <p className="flex items-center gap-1.5 text-xs text-muted-ink">
          <Pencil className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          Cliquez sur l'outil polygone (en haut à gauche de la carte) puis encadrez le secteur. Un seul secteur par ville/jour.
        </p>
        <div ref={containerRef} data-testid="pap-zone-map"
          className="h-[440px] w-full overflow-hidden rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)]" />
        {!ready && <p className="text-[11px] text-muted-ink">Chargement de la carte…</p>}
        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <Button variant="ghost" onClick={clearZone} disabled={!hasZone}
            className="text-[#D64545] hover:bg-[rgba(214,69,69,0.08)] hover:text-[#9B2226]" data-testid="pap-zone-clear">
            <Trash2 className="mr-1.5 h-4 w-4" /> Effacer la zone
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={save} disabled={saving}
              className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="pap-zone-save">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Enregistrer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
